import { useState } from 'react';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import {
  type GapNightParams,
  type RuleFormProps,
  majorStrToMinorOpt,
  minorToMajorStr,
  pctStrToNumOpt,
  intStrOpt,
} from '@/lib/pricingRules';

// gap_night: a SINGLE form (not a list). Fills orphan gaps up to
// `max_gap_nights` nights, optionally discounting them (pct and/or abs_minor)
// and optionally lowering min-stay to the gap length so the orphan night
// becomes bookable. Local string-draft state, seeded once from params.

interface Draft {
  maxGapNights: string;
  pct: string;
  abs: string;
  setMinStayToGap: boolean;
}

function paramsToDraft(p: GapNightParams): Draft {
  return {
    maxGapNights: p.max_gap_nights != null ? String(p.max_gap_nights) : '',
    pct: p.pct != null ? String(p.pct) : '',
    abs: minorToMajorStr(p.abs_minor),
    setMinStayToGap: p.set_min_stay_to_gap ?? false,
  };
}

function draftToParams(d: Draft): GapNightParams {
  const params: GapNightParams = { max_gap_nights: 0, set_min_stay_to_gap: d.setMinStayToGap };
  const maxGap = intStrOpt(d.maxGapNights);
  if (typeof maxGap === 'number') params.max_gap_nights = maxGap;
  const p = pctStrToNumOpt(d.pct);
  if (typeof p === 'number') params.pct = p;
  const a = majorStrToMinorOpt(d.abs);
  if (typeof a === 'number') params.abs_minor = a;
  return params;
}

export function GapNightRuleForm({ params, onChange }: RuleFormProps<GapNightParams>) {
  const [draft, setDraft] = useState<Draft>(() => paramsToDraft(params));

  function patch(p: Partial<Draft>) {
    const next = { ...draft, ...p };
    setDraft(next);
    onChange(draftToParams(next));
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Input
          label="Fill orphan gaps up to (nights)"
          type="number"
          min={1}
          step={1}
          value={draft.maxGapNights}
          onChange={(e) => patch({ maxGapNights: e.target.value })}
        />
        <Input
          label="Discount (%)"
          type="number"
          step="0.1"
          value={draft.pct}
          onChange={(e) => patch({ pct: e.target.value })}
          placeholder="optional"
        />
        <Input
          label="Absolute adjustment (€)"
          type="number"
          step="0.01"
          value={draft.abs}
          onChange={(e) => patch({ abs: e.target.value })}
          placeholder="optional"
        />
      </div>

      <Checkbox
        label="Lower min-stay to the gap length"
        description="So the orphan night is bookable on its own."
        checked={draft.setMinStayToGap}
        onChange={(e) => patch({ setMinStayToGap: e.target.checked })}
      />
    </div>
  );
}
